import { STOREFRONT_TOKEN, SITE_URL, SHOPIFY_HEADLESS_TOKEN, SHOPIFY_STOREFRONT_ID } from "utils/constants";
import { productFragment, collectionsFragment } from "shopify-queries/storefront/common";
// eslint-disable-next-line import/no-extraneous-dependencies
import {
    getTrackingValues,
    SHOPIFY_STOREFRONT_Y_HEADER,
    SHOPIFY_STOREFRONT_S_HEADER,
    SHOPIFY_STOREFRONT_ID_HEADER,
    SHOPIFY_UNIQUE_TOKEN_HEADER,
    SHOPIFY_VISIT_TOKEN_HEADER,
} from "@shopify/hydrogen-react";

/** Keep in sync with Storefront API version used elsewhere. */
export const STOREFRONT_API_VERSION = "2025-04";

export const getStorefrontToken = () => {
    const shouldUseHeadlessToken = typeof localStorage !== "undefined"
        && localStorage.getItem("useHeadlessToken") === "true";

    return shouldUseHeadlessToken === true ? SHOPIFY_HEADLESS_TOKEN : STOREFRONT_TOKEN;
};

const getStorefrontId = () => {
    const shouldUseHeadlessToken = typeof localStorage !== "undefined"
        && localStorage.getItem("useHeadlessToken") === "true";

    return shouldUseHeadlessToken ? SHOPIFY_STOREFRONT_ID : undefined;
};

/**
 * Prefer same-origin /api/... so Set-Cookie + server-timing land on the
 * storefront domain (Phase 3 Netlify edge proxy). On localhost (plain
 * `gatsby develop`), fall back to SITE_URL / checkout host — use `netlify dev`
 * for full proxy parity.
 */
export const getStorefrontApiUrl = (version = STOREFRONT_API_VERSION) => {
    const path = `/api/${version}/graphql.json`;
    if (typeof window === "undefined") {
        return `${(SITE_URL || "").replace(/\/+$/, "")}${path}`;
    }
    if (/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)) {
        const origin = (SITE_URL || "https://shop.sealskincovers.com").replace(/\/+$/, "");
        return `${origin}${path}`;
    }
    return path;
};

const storefrontQuery = async (query, variables) => {
    try {
        const { uniqueToken, visitToken } = getTrackingValues();
        const storefrontId = getStorefrontId();

        const response = await fetch(getStorefrontApiUrl(), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                "X-Shopify-Storefront-Access-Token": getStorefrontToken(),
                // Legacy Y/S headers (still accepted during transition)
                [SHOPIFY_STOREFRONT_Y_HEADER]: uniqueToken || "",
                [SHOPIFY_STOREFRONT_S_HEADER]: visitToken || "",
                // New token headers used by the cookie migration
                [SHOPIFY_UNIQUE_TOKEN_HEADER]: uniqueToken || "",
                [SHOPIFY_VISIT_TOKEN_HEADER]: visitToken || "",
                ...(storefrontId ? { [SHOPIFY_STOREFRONT_ID_HEADER]: storefrontId } : {}),
            },
            body: JSON.stringify({
                query,
                variables,
            }),
            // Include cookies on same-origin proxy responses
            credentials: "same-origin",
        });

        // Non-2xx responses can return non-JSON bodies (HTML error pages, empty
        // bodies, etc.). Always surface them as a structured response so callers
        // see `{ data: null, errors: [...] }` instead of either crashing on
        // `.data.xxx` access or silently receiving a raw Error object.
        if (!response.ok) {
            const body = await response.text().catch(() => "");
            return {
                data: null,
                errors: [{
                    message: `Storefront API responded with HTTP ${response.status} ${response.statusText || ""}`.trim(),
                    status: response.status,
                    body: typeof body === "string" ? body.slice(0, 1000) : undefined,
                }],
            };
        }

        return await response.json();
    } catch (err) {
        // Previously this returned the raw Error object, which broke every
        // caller that accessed `.data.xxx` (e.g. `data.cartCreate.cart.id`).
        // Return a consistent GraphQL-shaped failure so callers can detect and
        // log the failure without a misleading TypeError.
        return {
            data: null,
            errors: [{
                message: err?.message || "Storefront request failed",
                name: err?.name,
                stack: err?.stack,
            }],
        };
    }
};

export const getProducts = async (handles) => {
    /* Dynamically creates sub-queries like:
        product1: product(handle: "x-y") {
            productInfo...
        }
        product2: product(handle: "x-y") {
            productInfo...
        }
    Since Shopify currently doesn't support fetching multiple products by their handles
    */

    const productsByHandles = handles.map((handle, index) => `
        product${index}: product(handle: "${handle}") {
            ${productFragment}
        }
    `);

    const fetchProducts = await storefrontQuery(`
        query {
            ${productsByHandles}
        }
    `);

    return Object.values(fetchProducts.data || {});
};

export const getMediaImage = async (gIds) => {
    const mediaImages = await storefrontQuery(`
        query {
            nodes(ids: ${JSON.stringify(gIds)}) { 
                id
                ... on MediaImage { 
                image {
                    id 
                    url
               }
            }
          }
        }`);

    return Object.values(mediaImages.data || {});
};

export const getCollections = async (handles) => {
    const collectionsByHandle = handles.map((handle, index) => `
        collection${index}: collectionByHandle(handle: "${handle}") {
            ${collectionsFragment}
        }
    `);

    const collections = await storefrontQuery(`
    query { 
        ${collectionsByHandle}
    }`);

    return collections;
};

export default storefrontQuery;
